import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "clientId", "gutter", "lockPageScroll", "scrollbarStyle", "className", "showKeyLines", "includeScrollingIndication", "direction"];
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useThrottledCallback } from "@react-hookz/web";
import { getClientId, useForkRef, useIsScrolling, useRequestAnimationFrame, useResizeObserver } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import ScrollLock from "react-scrolllock";
import { scrollbarClassNames } from "../../../constants/scrollbars";
import { Keyline, KEYLINE_HEIGHT_PROPERTY_NAME, KEYLINE_WIDTH_PROPERTY_NAME } from "../styled/Keyline";
import { ScrollAreaScrollStyled } from "../styled/ScrollAreaScrollStyled";
import { ScrollAreaStyled } from "../styled/ScrollAreaStyled";
import { getScrollInfo } from "../utils/scrollUtils";
/**
 * ScrollArea is a component to handle a vertically scrolling content area. It is responsible for:
 * - Rendering the content area
 * - Providing a scrolling context for the content area
 * - Optionally locking browser scroll (useful for when being used inside a modal, so the page doesn't scroll behind it)
 * - Rendering an affordance that there's more content below the fold
 */
export var ScrollArea = /*#__PURE__*/forwardRef(function (_ref, providedRef) {
  var children = _ref.children,
    clientId = _ref.clientId,
    _ref$gutter = _ref.gutter,
    gutter = _ref$gutter === void 0 ? "legacy" : _ref$gutter,
    _ref$lockPageScroll = _ref.lockPageScroll,
    lockPageScroll = _ref$lockPageScroll === void 0 ? false : _ref$lockPageScroll,
    scrollbarStyle = _ref.scrollbarStyle,
    className = _ref.className,
    _ref$showKeyLines = _ref.showKeyLines,
    showKeyLines = _ref$showKeyLines === void 0 ? "end" : _ref$showKeyLines,
    _ref$includeScrolling = _ref.includeScrollingIndication,
    includeScrollingIndication = _ref$includeScrolling === void 0 ? true : _ref$includeScrolling,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? "vertical" : _ref$direction,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showStartKeyline = _useState2[0],
    setShowStartKeyline = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showEndKeyline = _useState4[0],
    setShowEndKeyline = _useState4[1];
  var _useIsScrolling = useIsScrolling(),
    isScrolling = _useIsScrolling.isScrolling,
    isScrollingRef = _useIsScrolling.ref;
  var scrollContainerRef = useRef(null);
  var scrollAreaRootRef = useRef(null);
  var combinedRootRef = useForkRef(scrollAreaRootRef, providedRef);
  var renderStartKeyline = (showKeyLines === "start" || showKeyLines === "both") && showStartKeyline;
  var renderEndKeyline = (showKeyLines === "end" || showKeyLines === "both") && showEndKeyline;
  var _useRequestAnimationF = useRequestAnimationFrame(),
    raf = _useRequestAnimationF.raf;
  var determineKeylines = useCallback(function () {
    raf(function () {
      var scrollContainer = scrollContainerRef.current;
      var scrollAreaRoot = scrollAreaRootRef.current;
      if (!scrollContainer || !scrollAreaRoot) {
        return;
      }
      var _getScrollInfo = getScrollInfo(scrollContainer),
        scrollTop = _getScrollInfo.scrollTop,
        scrollLeft = _getScrollInfo.scrollLeft,
        contentWidth = _getScrollInfo.contentWidth,
        contentHeight = _getScrollInfo.contentHeight,
        viewportHeight = _getScrollInfo.viewportHeight,
        viewportWidth = _getScrollInfo.viewportWidth;
      if (direction === "vertical") {
        setShowStartKeyline(scrollTop > 0);
        // We're going to remove 1px from the scrollable distance to account for any weird
        // sub-pixel math with zooming.
        var scrollableDistance = contentHeight - viewportHeight;
        setShowEndKeyline(scrollTop < scrollableDistance - 1);

        // Set the keyline's padding to the current width of the scrollContainer, minus it's left/right padding.
        var scrollContainerStyle = window.getComputedStyle(scrollContainer);
        var scrollContainerWidth = scrollContainer.clientWidth - parseFloat(scrollContainerStyle.paddingLeft) - parseFloat(scrollContainerStyle.paddingRight);
        scrollAreaRoot.style.setProperty(KEYLINE_WIDTH_PROPERTY_NAME, "".concat(scrollContainerWidth, "px"));
      } else {
        setShowStartKeyline(scrollLeft > 0);
        // We're going to remove 1px from the scrollable distance to account for any weird
        // sub-pixel math with zooming.
        var _scrollableDistance = contentWidth - viewportWidth;
        setShowEndKeyline(scrollLeft < _scrollableDistance - 1);

        // Set the keyline's padding to the current width of the scrollContainer, minus it's left/right padding.
        var _scrollContainerStyle = window.getComputedStyle(scrollContainer);
        var scrollContainerHeight = scrollContainer.clientHeight - parseFloat(_scrollContainerStyle.paddingTop) - parseFloat(_scrollContainerStyle.paddingBottom);
        scrollAreaRoot.style.setProperty(KEYLINE_HEIGHT_PROPERTY_NAME, "".concat(scrollContainerHeight, "px"));
      }
    });
  }, [raf, direction]);

  // Attach handlers to the scroll container's current element to show/hide keylines.
  useEffect(function () {
    var containerElement = scrollContainerRef.current;
    if (containerElement && showKeyLines !== "none") {
      // Run this initially to determine if we need to show the keyline.
      determineKeylines();
      containerElement.addEventListener("scroll", determineKeylines, {
        passive: true
      });
      return function () {
        containerElement.removeEventListener("scroll", determineKeylines);
      };
    }
  }, [determineKeylines, showKeyLines]);

  // We also need to evaluate the keyline's visibility if the container resizes.
  var _useResizeObserver = useResizeObserver(useCallback(function () {
      var containerElement = scrollContainerRef.current;
      if (containerElement) {
        determineKeylines();
      }
    }, [determineKeylines]), {
      enabled: showKeyLines !== "none"
    }),
    resizeObserverRef = _useResizeObserver.ref;
  var combinedRef = useForkRef(scrollContainerRef, isScrollingRef, resizeObserverRef);
  var timerId = useRef(null);

  // Fix for https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/589.
  // We need to quickly toggle the ScrollLock component off/on when the window resizes. This
  // causes the ScrollLock component to re-calculate the window height and update the CSS
  // properties it applies to the body when scrolling is locked.
  var _useState5 = useState(lockPageScroll),
    _useState6 = _slicedToArray(_useState5, 2),
    localScrollLockIsActive = _useState6[0],
    setLocalScrollIsActive = _useState6[1];
  var throttledHandleResize = useThrottledCallback(function () {
    setLocalScrollIsActive(false);
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
    timerId.current = setTimeout(function () {
      setLocalScrollIsActive(true);
    });
  }, [], 30);
  useEffect(function () {
    window.addEventListener("resize", throttledHandleResize);
    return function () {
      window.removeEventListener("resize", throttledHandleResize);
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
    };
  }, [throttledHandleResize]);
  return jsx(ScrollAreaStyled, _extends({
    className: className
  }, rest, {
    ref: combinedRootRef
  }), jsx(ScrollAreaScrollStyled, {
    direction: direction,
    "data-client-id": clientId,
    gutter: gutter,
    ref: combinedRef,
    className: scrollbarStyle && scrollbarClassNames[scrollbarStyle]
  }, children, lockPageScroll && jsx(ScrollLock, {
    isActive: localScrollLockIsActive
  })), renderStartKeyline && jsx(Keyline, {
    direction: direction,
    "data-client-id": getClientId(clientId, "scrollarea-keyline-top"),
    isVisible: showStartKeyline,
    gutter: gutter,
    isScrolling: isScrolling,
    includeScrollingIndication: includeScrollingIndication,
    alignTop: true
  }), renderEndKeyline && jsx(Keyline, {
    direction: direction,
    "data-client-id": getClientId(clientId, "lodestar-keyline"),
    isVisible: showEndKeyline,
    gutter: gutter,
    isScrolling: isScrolling,
    includeScrollingIndication: includeScrollingIndication
  }));
});
ScrollArea.displayName = "ScrollArea";
//# sourceMappingURL=ScrollArea.js.map